(() => {
  const SUCCESS_DIALOG_ID = "LGCSupportSuccessDialog";
  const SUPPORT_SUCCESS_PARAM = "lgc_support_submitted";
  const SUPPORT_SUCCESS_VALUE = "1";

  const readTranslations = () => {
    const el = document.getElementById("LGCSupportTranslations");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (_) {
      return {};
    }
  };

  const i18n = readTranslations();
  const t = (key, fallback) => i18n[key] || fallback;

  const createSuccessDialog = () => {
    const existingDialog = document.getElementById(SUCCESS_DIALOG_ID);
    if (existingDialog) {
      return existingDialog;
    }

    const dialog = document.createElement("div");
    dialog.id = SUCCESS_DIALOG_ID;
    dialog.className = "lgc-support-success-dialog";
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-labelledby", "LGCSupportSuccessDialogTitle");
    dialog.hidden = true;

    dialog.innerHTML = `
      <div class="lgc-support-success-dialog__overlay" data-support-success-close></div>
      <div class="lgc-support-success-dialog__card" role="document">
        <button
          type="button"
          class="lgc-support-success-dialog__close"
          aria-label="${t("closeMessage", "Luk besked")}"
          data-support-success-close
        >
          <span aria-hidden="true">×</span>
        </button>
        <h3 id="LGCSupportSuccessDialogTitle" class="lgc-support-success-dialog__title"></h3>
        <p class="lgc-support-success-dialog__message"></p>
      </div>
    `;

    document.body.appendChild(dialog);

    dialog.querySelectorAll("[data-support-success-close]").forEach((closer) => {
      closer.addEventListener("click", () => {
        dialog.hidden = true;
        document.body.classList.remove("lgc-support-success-open");
      });
    });

    return dialog;
  };

  const showSupportSuccessDialog = (ticketId = "xxxx") => {
    const dialog = createSuccessDialog();
    const titleEl = dialog.querySelector(".lgc-support-success-dialog__title");
    const messageEl = dialog.querySelector(".lgc-support-success-dialog__message");
    const safeTicketId = String(ticketId || "xxxx").trim() || "xxxx";

    if (titleEl) {
      titleEl.textContent = `${t("ticketReceivedPrefix", "Modtagelse af supportsag")} ${safeTicketId}`;
    }

    if (messageEl) {
      messageEl.textContent = t(
        "thankYouMessage",
        "Tak for at kontakte os. Din supportsag er nu oprettet, og vi vender tilbage hurtigst muligt - typisk inden for 1-3 arbejdsdage."
      );
    }

    dialog.hidden = false;
    document.body.classList.add("lgc-support-success-open");
  };

  const setSubmissionStatus = (message, type = "info") => {
    const el = document.getElementById("LGCSupportSubmissionStatus");
    if (!el) {
      return;
    }

    if (!message) {
      el.style.display = "none";
      el.textContent = "";
      return;
    }

    const palette = {
      info: { background: "#eef3ff", color: "#1c3b8f", border: "#c9d7ff" },
      success: { background: "#e9f9ef", color: "#0f5f2f", border: "#b6e6c6" },
      error: { background: "#fff1f1", color: "#8a1f1f", border: "#ffc9c9" },
    };

    const style = palette[type] || palette.info;
    el.style.display = "block";
    el.style.margin = "0 0 16px";
    el.style.padding = "12px 14px";
    el.style.border = `1px solid ${style.border}`;
    el.style.borderRadius = "8px";
    el.style.background = style.background;
    el.style.color = style.color;
    el.style.fontWeight = "600";
    el.textContent = message;
  };

  const showSubmissionFeedback = () => {
    const params = new URLSearchParams(window.location.search);
    const attempted = sessionStorage.getItem("lgcSupportSubmitAttempt") === "1";
    const hasContactPosted = params.get("contact_posted") === "true";
    const hasSupportSuccessMarker = params.get(SUPPORT_SUCCESS_PARAM) === SUPPORT_SUCCESS_VALUE;

    if (!attempted && !hasSupportSuccessMarker && !hasContactPosted) {
      return;
    }

    if (hasContactPosted || hasSupportSuccessMarker) {
      const ticketId = params.get("ticket") || params.get("support_case") || params.get("case") || "xxxx";
      setSubmissionStatus(
        t("formSent", "Supportformular sendt til Shopify. Tjek venligst support-indbakken."),
        "success"
      );
      showSupportSuccessDialog(ticketId);

      if (window.history && typeof window.history.replaceState === "function") {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete(SUPPORT_SUCCESS_PARAM);
        cleanUrl.searchParams.delete("contact_posted");
        window.history.replaceState({}, document.title, cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
      }

      sessionStorage.removeItem("lgcSupportSubmitAttempt");
      return;
    }

    // Clear stale flag when no explicit success marker is available.
    sessionStorage.removeItem("lgcSupportSubmitAttempt");
  };

  const initSupportCenter = (container) => {
    if (!container || container.dataset.lgcSupportInit === "true") {
      return;
    }

    container.dataset.lgcSupportInit = "true";

    const uploadConfigEl = container.querySelector("[data-support-upload-config]");
    const uploadConfig = {
      showImageFields: uploadConfigEl?.dataset.showImageFields === "true",
      enabled: uploadConfigEl?.dataset.enabled === "true",
      cloudName: (uploadConfigEl?.dataset.cloudName || "").trim(),
      uploadPreset: (uploadConfigEl?.dataset.uploadPreset || "").trim(),
      folder: (uploadConfigEl?.dataset.folder || "").trim(),
    };

    const categoryData = {};
    const categoryForms = {};
    const storedView = sessionStorage.getItem("lgcSupportView") || "landing";
    const currentView = storedView === "content" ? "support-menu" : storedView;
    const currentCategory = sessionStorage.getItem("lgcSupportCategory") || "psa-grading";

    const loadCategoryData = () => {
      const contentDivs = container.querySelectorAll("[data-category-content]");
      contentDivs.forEach((div) => {
        const category = div.dataset.categoryContent;
        const title = div.querySelector("[data-title]")?.textContent || "";
        const body = div.querySelector("[data-body]")?.innerHTML || "";
        categoryData[category] = { title, body };
      });

      const formTemplates = container.querySelectorAll("[data-category-form]");
      formTemplates.forEach((template) => {
        const category = template.dataset.categoryForm;
        categoryForms[category] = template.innerHTML;
      });
    };

    const setActiveCategory = (category) => {
      container.querySelectorAll('[data-action="select-category"]').forEach((button) => {
        const isActive = button.dataset.category === category;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    };

    const showView = (viewName) => {
      container.querySelectorAll("[data-view]").forEach((view) => {
        view.classList.remove("is-active");
      });

      const targetView = container.querySelector(`[data-view="${viewName}"]`);
      if (targetView) {
        targetView.classList.add("is-active");
      }
    };

    const displayCategory = (category) => {
      const data = categoryData[category];
      if (!data) {
        return;
      }

      const titleEl = container.querySelector("[data-content-title]");
      const bodyEl = container.querySelector("[data-content-body]");
      const formEl = container.querySelector("[data-content-form]");

      if (titleEl) {
        titleEl.textContent = data.title;
      }

      if (bodyEl) {
        bodyEl.innerHTML = data.body;
      }

      if (formEl) {
        formEl.innerHTML = categoryForms[category] || "";
        attachFormHandlers(formEl, category, data.title);
      }

      setActiveCategory(category);
    };

    const attachFormHandlers = (formEl, category, categoryTitle) => {
      const form = formEl.querySelector(".lgc-support-form");
      if (!form) {
        return;
      }

      if (!uploadConfig.showImageFields) {
        form.querySelectorAll('input[type="file"]').forEach((fileInput) => {
          fileInput.disabled = true;
          const group = fileInput.closest(".lgc-support-form__group");
          if (group) {
            group.style.display = "none";
          }
        });
      }

      const ensureUploadStatusEl = () => {
        let statusEl = form.querySelector("[data-lgc-upload-status]");
        if (statusEl) {
          return statusEl;
        }

        statusEl = document.createElement("p");
        statusEl.setAttribute("data-lgc-upload-status", "true");
        statusEl.className = "lgc-support-form__upload-status";
        statusEl.style.display = "none";
        const firstUpload = form.querySelector(".lgc-support-form__upload");
        if (firstUpload?.parentElement) {
          firstUpload.parentElement.appendChild(statusEl);
        } else {
          form.appendChild(statusEl);
        }
        return statusEl;
      };

      const setUploadStatus = (message, type = "info") => {
        const statusEl = ensureUploadStatusEl();
        if (!message) {
          statusEl.style.display = "none";
          statusEl.textContent = "";
          statusEl.classList.remove("is-error", "is-success", "is-info");
          return;
        }

        statusEl.style.display = "block";
        statusEl.textContent = message;
        statusEl.classList.remove("is-error", "is-success", "is-info");
        statusEl.classList.add(type === "error" ? "is-error" : type === "success" ? "is-success" : "is-info");
      };

      const bindUploadTriggers = () => {
        if (!uploadConfig.showImageFields) {
          return;
        }

        form.querySelectorAll(".lgc-support-form__upload[for]").forEach((trigger) => {
          if (trigger.dataset.lgcUploadInit === "true") {
            return;
          }

          trigger.dataset.lgcUploadInit = "true";

          const fileInputId = trigger.getAttribute("for");
          const fileInput = fileInputId ? form.querySelector(`#${fileInputId}`) : null;
          if (!fileInput) {
            return;
          }

          const updateUploadText = () => {
            const textEl = trigger.querySelector(".lgc-support-form__upload-text");
            if (!textEl) {
              return;
            }

            const names = Array.from(fileInput.files || [])
              .map((file) => file.name)
              .filter(Boolean);

            textEl.textContent = names.length
              ? names.join(", ")
              : t("dropFiles", "Træk og slip billedet her, eller vælg filer");
          };

          trigger.addEventListener("click", (event) => {
            event.preventDefault();
            fileInput.click();
          });

          trigger.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") {
              return;
            }

            event.preventDefault();
            fileInput.click();
          });

          fileInput.addEventListener("change", updateUploadText);
        });
      };

      const ensureHiddenField = (fieldName, fieldType = "input") => {
        let field = form.querySelector(`[data-lgc-contact-field="${fieldName}"]`);
        if (field) {
          return field;
        }

        field = document.createElement(fieldType === "textarea" ? "textarea" : "input");
        if (fieldType !== "textarea") {
          field.type = "hidden";
        }
        field.name = fieldName;
        field.setAttribute("data-lgc-contact-field", fieldName);
        form.appendChild(field);
        return field;
      };

      const removeLegacyTextareaField = (fieldName) => {
        form.querySelectorAll(`textarea[data-lgc-contact-field="${fieldName}"]`).forEach((field) => {
          field.remove();
        });
      };

      form.method = "post";
      form.action = "/contact";
      form.acceptCharset = "UTF-8";

      const formTypeField = ensureHiddenField("form_type");
      const utf8Field = ensureHiddenField("utf8");
      const returnToField = ensureHiddenField("return_to");
      const recipientField = ensureHiddenField("contact[recipient]");
      const contactNameField = ensureHiddenField("contact[name]");
      const contactEmailField = ensureHiddenField("contact[email]");
      const contactPhoneField = ensureHiddenField("contact[phone]");
      const subjectField = ensureHiddenField("contact[subject]");
      const categoryField = ensureHiddenField("contact[category]");
      const sourceUrlField = ensureHiddenField("contact[source_url]");
      removeLegacyTextareaField("contact[details]");
      removeLegacyTextareaField("contact[body]");
      const detailsField = ensureHiddenField("contact[details]");
      const bodyField = ensureHiddenField("contact[body]");

      formTypeField.value = "contact";
      utf8Field.value = "✓";
      recipientField.value = "support@letsgradecards.dk";
      bindUploadTriggers();

      const getFieldLabel = (field) => {
        if (!field) {
          return "";
        }

        const byId = field.id ? form.querySelector(`label[for="${field.id}"]`) : null;
        if (byId) {
          return byId.textContent.trim();
        }

        const wrappedLabel = field.closest("label");
        if (wrappedLabel) {
          return wrappedLabel.textContent.trim();
        }

        if (field.name) {
          return field.name;
        }

        if (field.id) {
          return field.id;
        }

        return field.placeholder || "Felt";
      };

      const buildSupportMessage = () => {
        const lines = [];
        const seenRadioGroups = new Set();
        const excludedContactFields = new Set([
          "name",
          "email",
          "phone",
          "contact[name]",
          "contact[email]",
          "contact[phone]",
        ]);

        const fields = form.querySelectorAll("input, textarea, select");
        fields.forEach((field) => {
          if (field.type === "submit" || field.type === "button" || field.type === "hidden") {
            return;
          }

          if (excludedContactFields.has(field.name)) {
            return;
          }

          if (field.type === "file") {
            const fileNames = Array.from(field.files || [])
              .map((file) => file.name)
              .join(", ");
            if (fileNames) {
              lines.push(`${getFieldLabel(field)}: ${fileNames}`);
            }
            return;
          }

          if (field.type === "radio") {
            if (!field.name || seenRadioGroups.has(field.name)) {
              return;
            }

            seenRadioGroups.add(field.name);
            const checked = form.querySelector(`input[type="radio"][name="${field.name}"]:checked`);
            if (!checked) {
              return;
            }

            const checkedLabel = checked.closest("label")?.textContent.trim();
            lines.push(`${getFieldLabel(field)}: ${checkedLabel || checked.value || "Valgt"}`);
            return;
          }

          if (field.type === "checkbox") {
            if (!field.checked) {
              return;
            }

            lines.push(`${getFieldLabel(field)}: Ja`);
            return;
          }

          const value = (field.value || "").trim();
          if (!value) {
            return;
          }

          lines.push(`${getFieldLabel(field)}: ${value}`);
        });

        return lines.join("\n");
      };

      const uploadImagesToCloudinary = async () => {
        if (!uploadConfig.enabled || !uploadConfig.cloudName || !uploadConfig.uploadPreset) {
          return [];
        }

        const fileInputs = Array.from(form.querySelectorAll('input[type="file"]'));
        const files = fileInputs.flatMap((input) => Array.from(input.files || []));
        if (!files.length) {
          return [];
        }

        const uploadEndpoint = `https://api.cloudinary.com/v1_1/${uploadConfig.cloudName}/image/upload`;
        const uploadTasks = files.map(async (file) => {
          const payload = new FormData();
          payload.append("file", file);
          payload.append("upload_preset", uploadConfig.uploadPreset);
          if (uploadConfig.folder) {
            payload.append("folder", uploadConfig.folder);
          }

          const response = await fetch(uploadEndpoint, {
            method: "POST",
            body: payload,
          });

          if (!response.ok) {
            throw new Error(`Upload failed: ${response.status}`);
          }

          const data = await response.json();
          return data.secure_url || "";
        });

        const urls = await Promise.all(uploadTasks);
        return urls.filter(Boolean);
      };

      form.addEventListener("submit", (e) => {
        if (form.dataset.lgcSubmitting === "true") {
          delete form.dataset.lgcSubmitting;
          return;
        }

        const submitBtn = form.querySelector(".lgc-support-form__submit");
        let submitWatchdogTimer = null;

        const restoreVisibleSubmitButton = () => {
          const visibleSubmitBtn = form.querySelector(".lgc-support-form__submit");
          if (!visibleSubmitBtn) {
            return;
          }

          visibleSubmitBtn.disabled = false;
          visibleSubmitBtn.textContent = visibleSubmitBtn.dataset.originalText || "INDSEND SUPPORTSAG";
        };

        const resetSubmitButton = () => {
          if (!submitBtn) {
            restoreVisibleSubmitButton();
            return;
          }

          submitBtn.disabled = false;
          submitBtn.textContent = submitBtn.dataset.originalText || "INDSEND SUPPORTSAG";
          restoreVisibleSubmitButton();
        };

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.dataset.originalText = submitBtn.textContent;
          submitBtn.textContent = "SENDER...";
        }

        const formData = new FormData(form);
        const name = String(formData.get("name") || "").trim();
        const email = String(formData.get("email") || "").trim();
        const phone = String(formData.get("phone") || "").trim();

        if (!name || !email) {
          e.preventDefault();
          alert(t("fillNameEmail", "Udfyld venligst navn og email før du indsender supportsagen."));
          resetSubmitButton();
          return;
        }

        // Start watchdog before submit attempt so it still fires if bridge submit interrupts normal flow.
        submitWatchdogTimer = window.setTimeout(() => {
          if (!submitBtn || !submitBtn.disabled) {
            return;
          }

          resetSubmitButton();
          setSubmissionStatus(
            t(
              "submitFailedCaptcha",
              "Afsendelsen blev ikke gennemfort. Shopify kan have afvist captcha-token. Opdater siden og prov igen."
            ),
            "error"
          );
          alert(t("submitFailed", "Afsendelsen blev ikke gennemfort. Opdater siden og prov igen."));
        }, 12000);

        const messageBody = buildSupportMessage();
        const supportMessage = [`Kategori: ${categoryTitle}`, "Se feltet 'Details' for alle sagsdetaljer."].join(
          "\n\n"
        );
        const supportSubject = `${t("subjectPrefix", "Supportsag -")} ${categoryTitle}`;

        const finalizeAndSubmit = (finalMessageBody, triggerNativeSubmit) => {
          const returnUrl = new URL(window.location.href);
          returnUrl.searchParams.set(SUPPORT_SUCCESS_PARAM, SUPPORT_SUCCESS_VALUE);
          returnToField.value = returnUrl.pathname + returnUrl.search + returnUrl.hash;
          contactNameField.value = name;
          contactEmailField.value = email;
          contactPhoneField.value = phone;
          subjectField.value = supportSubject;
          categoryField.value = categoryTitle;
          sourceUrlField.value = window.location.href;
          detailsField.value = finalMessageBody;
          bodyField.value = supportMessage;

          sessionStorage.setItem("lgcSupportSubmitAttempt", "1");
          sessionStorage.removeItem("lgcSupportView");
          sessionStorage.removeItem("lgcSupportCategory");

          if (triggerNativeSubmit) {
            form.dataset.lgcSubmitting = "true";
            if (typeof form.requestSubmit === "function") {
              form.requestSubmit(submitBtn || undefined);
            } else {
              form.submit();
            }
          }
        };

        if (uploadConfig.enabled && uploadConfig.cloudName && uploadConfig.uploadPreset) {
          const hasImages = Array.from(form.querySelectorAll('input[type="file"]')).some(
            (input) => (input.files || []).length > 0
          );

          if (hasImages) {
            e.preventDefault();
            if (submitBtn) {
              submitBtn.textContent = t("uploadingButton", "UPLOADER...");
            }
            setUploadStatus(t("uploadingStatus", "Uploader billeder..."), "info");

            uploadImagesToCloudinary()
              .then((urls) => {
                let finalMessageBody = messageBody;
                if (urls.length) {
                  const imageLines = urls.map((url, index) => `${index + 1}. ${url}`);
                  finalMessageBody = [messageBody, "Billeder:", ...imageLines].filter(Boolean).join("\n");
                }

                setUploadStatus(t("uploadSuccess", "Billeder er uploadet. Sender supportsag..."), "success");

                finalizeAndSubmit(finalMessageBody, true);
              })
              .catch(() => {
                if (submitWatchdogTimer) {
                  window.clearTimeout(submitWatchdogTimer);
                }
                resetSubmitButton();
                setUploadStatus(
                  t("uploadFailedStatus", "Upload af billeder fejlede. Tjek indstillinger og prøv igen."),
                  "error"
                );
                setSubmissionStatus(
                  t(
                    "uploadFailedSubmission",
                    "Upload af billeder fejlede. Tjek Cloudinary indstillinger og prov igen."
                  ),
                  "error"
                );
                alert(
                  t("uploadFailedSubmission", "Upload af billeder fejlede. Tjek Cloudinary indstillinger og prov igen.")
                );
              });
            return;
          }
        }

        setUploadStatus("");
        finalizeAndSubmit(messageBody, false);
      });
    };

    container.querySelectorAll("[data-action]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const action = e.currentTarget.dataset.action;

        if (action === "go-support" || action === "go-other") {
          showView("support-menu");
          sessionStorage.setItem("lgcSupportView", "support-menu");
          displayCategory(sessionStorage.getItem("lgcSupportCategory") || "psa-grading");
          return;
        }

        if (action === "go-back") {
          showView("landing");
          sessionStorage.removeItem("lgcSupportView");
          return;
        }

        if (action === "select-category") {
          const category = e.currentTarget.dataset.category;
          sessionStorage.setItem("lgcSupportCategory", category);
          displayCategory(category);
          showView("support-menu");
          sessionStorage.setItem("lgcSupportView", "support-menu");
        }
      });
    });

    loadCategoryData();
    showView(currentView);

    if (currentView === "support-menu" || currentView === "content") {
      displayCategory(currentCategory);
    } else if (currentView === "landing") {
      setActiveCategory(currentCategory);
    }
  };

  const initAllSupportCenters = (scope = document) => {
    showSubmissionFeedback();
    scope.querySelectorAll("[data-support-center]").forEach((container) => {
      initSupportCenter(container);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAllSupportCenters());
  } else {
    initAllSupportCenters();
  }

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      return;
    }

    const dialog = document.getElementById(SUCCESS_DIALOG_ID);
    if (!dialog || dialog.hidden) {
      return;
    }

    dialog.hidden = true;
    document.body.classList.remove("lgc-support-success-open");
  });

  document.addEventListener("shopify:section:load", (event) => {
    initAllSupportCenters(event.target || document);
  });
})();
